// Admin: list of print templates.  CRUD-tile that links to the detail
// editor for each template + a wizard to create new ones.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { RequirePermission } from "@/components/RequirePermission";
import { ErrorState, LoadingState } from "@/components/States";
import { Toggle } from "@/components/Toggle";
import { printAdminApi, printApi, type PrintTemplateRead } from "@/lib/api";

export default function PrintTemplatesList() {
  return (
    <RequirePermission codes="print.template.manage">
      <Inner />
    </RequirePermission>
  );
}

function Inner() {
  const { t } = useTranslation();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [creating, setCreating] = useState(false);

  const q = useQuery({
    queryKey: ["print", "templates", "admin", { includeArchived }],
    queryFn: () => printApi.listTemplates({ include_archived: includeArchived }),
  });

  return (
    <>
      <PageHeader
        eyebrow={t("eyebrows.adminPrint")}
        title={t("printTemplates.title")}
        description={t("printTemplates.description")}
      />

      <section className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 text-sm text-text-muted">
          <Toggle
            checked={includeArchived}
            onChange={setIncludeArchived}
            label={t("printTemplates.showArchived")}
          />
          <span
            className="cursor-pointer select-none"
            onClick={() => setIncludeArchived((v) => !v)}
          >
            {t("printTemplates.showArchived")}
          </span>
        </div>
        <button className="btn btn-primary" onClick={() => setCreating(true)}>
          {t("printTemplates.newTemplate")}
        </button>
      </section>

      {q.isLoading && <LoadingState />}
      {q.isError && <ErrorState error={q.error} />}

      {q.data && q.data.items.length === 0 && (
        <section className="card p-8 text-center text-text-muted">
          <p>{t("printTemplates.empty")}</p>
          <p className="text-xs mt-2">{t("printTemplates.emptyHint")}</p>
        </section>
      )}

      {q.data && q.data.items.length > 0 && (
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {q.data.items.map((tpl) => (
            <TemplateCard key={tpl.id} t={tpl} />
          ))}
        </section>
      )}

      {creating && <CreateModal onClose={() => setCreating(false)} />}
    </>
  );
}

function TemplateCard({ t: tpl }: { t: PrintTemplateRead }) {
  const { t } = useTranslation();
  return (
    <Link
      to={`/admin/print-templates/${tpl.id}`}
      className="card p-5 hover:border-accent transition-colors block"
    >
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-mono text-xs uppercase tracking-widest text-text-muted">
            {tpl.brand} · {tpl.kind}
          </p>
          <h3 className="text-base mt-1 truncate">{tpl.name}</h3>
          {tpl.description && (
            <p className="text-text-muted text-sm mt-1 line-clamp-2">{tpl.description}</p>
          )}
        </div>
        {tpl.is_archived && (
          <span className="pill pill-warn font-mono text-xs whitespace-nowrap">{t("printTemplates.archived")}</span>
        )}
      </header>
      <p className="text-text-muted text-xs font-mono mt-3">
        {tpl.page_width_mm}×{tpl.page_height_mm} {t("printTemplates.mm")} · v{tpl.version}
      </p>
    </Link>
  );
}

function CreateModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [form, setForm] = useState({
    kind: "",
    brand: "",
    name: "",
    description: "",
    page_width_mm: "210",
    page_height_mm: "297",
  });
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      printAdminApi.create({
        kind: form.kind.trim(),
        brand: form.brand.trim(),
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        page_width_mm: Number(form.page_width_mm),
        page_height_mm: Number(form.page_height_mm),
      }),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: ["print", "templates"] });
      // Navigate to the detail editor for the freshly created template.
      window.location.assign(`/admin/print-templates/${created.id}`);
    },
    onError: (e) => setErr(e instanceof Error ? e.message : t("common.error")),
  });

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="card max-w-lg w-full p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <h2 className="text-lg">{t("printTemplates.modalTitle")}</h2>
          <p className="text-text-muted text-sm mt-1">
            {t("printTemplates.modalHint")}
          </p>
        </header>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setErr(null);
            mut.mutate();
          }}
          className="space-y-3"
        >
          <Field label="kind (slug)" hint={t("printTemplates.kindHint")}>
            <input
              type="text"
              required
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value })}
              className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm"
              placeholder="passport"
              pattern="^[a-z][a-z0-9-]*$"
            />
          </Field>
          <Field label="brand">
            <input
              type="text"
              required
              value={form.brand}
              onChange={(e) => setForm({ ...form, brand: e.target.value })}
              className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm"
              placeholder="ECTC"
            />
          </Field>
          <Field label={t("printTemplates.nameLabel")}>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm"
              placeholder={t("printTemplates.namePlaceholder")}
            />
          </Field>
          <Field label={t("printTemplates.descLabel")}>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm"
              rows={2}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("printTemplates.widthMm")}>
              <input
                type="number"
                step="any"
                required
                value={form.page_width_mm}
                onChange={(e) => setForm({ ...form, page_width_mm: e.target.value })}
                className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm font-mono tabular-nums"
              />
            </Field>
            <Field label={t("printTemplates.heightMm")}>
              <input
                type="number"
                step="any"
                required
                value={form.page_height_mm}
                onChange={(e) => setForm({ ...form, page_height_mm: e.target.value })}
                className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm font-mono tabular-nums"
              />
            </Field>
          </div>

          {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" className="btn" onClick={onClose}>
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn btn-primary" disabled={mut.isPending}>
              {mut.isPending ? t("common.creating") : t("common.create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <p className="text-text-muted text-xs mt-1">{hint}</p>}
    </label>
  );
}
